import './styles/contact.scss';

import { DroletButton, TitleBanner } from '../components/Primitives';

import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import React from 'react';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import { graphql } from 'gatsby';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';

export const query = graphql`
    query {
        metadata: allProductsJson(filter: { code: { eq: "drolet_contact" } }) {
            nodes {
                code
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                fields {
                    banniere_desktop {
                        childImageSharp {
                            gatsbyImageData(layout: CONSTRAINED)
                        }
                    }
                    banniere_mobile {
                        childImageSharp {
                            gatsbyImageData(layout: CONSTRAINED)
                        }
                    }
                }
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        customerServiceInfo: allProductsJson(
            filter: { code: { eq: "drolet_customer-service" } }
        ) {
            nodes {
                ...contactUsInformationFragment
            }
        }
        technicalSupportInfo: allProductsJson(
            filter: { code: { eq: "drolet_technical-support" } }
        ) {
            nodes {
                ...contactUsInformationFragment
            }
        }
        retailerServiceInfo: allProductsJson(
            filter: { code: { eq: "drolet_retailer-service" } }
        ) {
            nodes {
                ...contactUsInformationFragment
            }
        }
    }
`;

export default function ContactUs({ data }) {
    const { t } = useTranslation('pages');
    const { currentLocale, navigateTranslated } = usePageLocalization();
    const cms = data?.metadata?.nodes[0];
    const cmsAttributes = cms?.attributes;
    const customerServiceInfo = data?.customerServiceInfo?.nodes?.[0];
    const technicalSupportInfo = data?.technicalSupportInfo?.nodes?.[0];
    const retailerServiceInfo = data?.retailerServiceInfo?.nodes?.[0];

    const metadata = {
        title: cmsAttributes?.meta_title?.values[0][currentLocale],
        description: cmsAttributes?.meta_description?.values[0][currentLocale],
    };

    const responsiveImages = data?.metadata?.nodes?.[0]?.fields;
    const desktopImage =
        responsiveImages?.banniere_desktop[0]?.childImageSharp?.gatsbyImageData;
    const mobileImage =
        responsiveImages?.banniere_mobile[0]?.childImageSharp?.gatsbyImageData;
    const isMobile = useMobileDetect().isMobile();
    const pageTitle = data?.metadata?.nodes?.[0]?.title?.[currentLocale];

    const serviceTitle = customerServiceInfo?.title?.[currentLocale];
    const techTitle = technicalSupportInfo?.title?.[currentLocale];
    const retailTitle = retailerServiceInfo?.title?.[currentLocale];

    const customerServicePhone =
        customerServiceInfo?.attributes?.texte_1?.values?.[0]?.[currentLocale];
    const technicalSupportPhone =
        technicalSupportInfo?.attributes?.texte_1?.values?.[0]?.[currentLocale];
    const retailerServicePhone =
        retailerServiceInfo?.attributes?.texte_1?.values?.[0]?.[currentLocale];

    const customerServiceOpeningDays =
        customerServiceInfo?.attributes?.texte_2?.values?.[0]?.[currentLocale];
    const technicalSupportOpeningDays =
        technicalSupportInfo?.attributes?.texte_2?.values?.[0]?.[currentLocale];
    const retailerServiceOpeningDays =
        retailerServiceInfo?.attributes?.texte_2?.values?.[0]?.[currentLocale];

    const customerServiceOpeningHours =
        customerServiceInfo?.attributes?.texte_3?.values?.[0]?.[currentLocale];
    const technicalSupportOpeningHours =
        technicalSupportInfo?.attributes?.texte_3?.values?.[0]?.[currentLocale];
    const retailerServiceOpeningHours =
        retailerServiceInfo?.attributes?.texte_3?.values?.[0]?.[currentLocale];

    return (
        <Layout>
            <SEO title={metadata?.title} description={metadata?.description} />
            <TitleBanner
                className='accessory-listing-titlebanner'
                imageData={isMobile ? mobileImage : desktopImage}
                title={pageTitle}
            />
            <div className='container contact'>
                <div className='contact-header'>
                    <div className='contact-title'>
                        <div className='title-small'>
                            {t('contact.communicate-with')}
                        </div>
                        <div className='title-big'>
                            {t('contact.our-experts')}
                        </div>
                        <div className='line'></div>
                    </div>
                    <div
                        className='blog-link'
                        onClick={() => navigateTranslated('/faq/wood-stoves/')}
                    >
                        <span>{t('contact.faq-link')}</span>
                    </div>
                </div>
                <div className='contactWrapper'>
                    <div className='contactCategory'>
                        <div className='information'>
                            <h3>{serviceTitle}</h3>
                            <span className='info'>
                                {t('contact.upcoming-purchase')}
                            </span>
                            <p>
                                <a href={`tel:${customerServicePhone}`}>
                                    {customerServicePhone}
                                </a>

                                <br />
                                <div className='openingDays'>
                                    {customerServiceOpeningDays}
                                    <br />
                                    {customerServiceOpeningHours}
                                </div>
                            </p>
                        </div>
                        <DroletButton
                            link='/contact/customer-service/'
                            className='contactBtn'
                            label={t('contact.contact-form')}
                        />
                    </div>

                    <div className='contactCategory'>
                        <div className='information'>
                            <h3>{techTitle}</h3>
                            <span className='info'>
                                {t('contact.previous-purchase')}
                            </span>
                            <p>
                                <a href={`tel:${technicalSupportPhone}`}>
                                    {technicalSupportPhone}
                                </a>

                                <br />
                                <div className='openingDays'>
                                    {technicalSupportOpeningDays}
                                    <br />
                                    {technicalSupportOpeningHours}
                                </div>
                            </p>
                        </div>
                        <DroletButton
                            link='/contact/technical-support/'
                            className='contactBtn'
                            label={t('contact.contact-form')}
                        />
                    </div>

                    <div className='contactCategory'>
                        <div className='information'>
                            <h3>{retailTitle}</h3>
                            <p>
                                <a href={`tel:${retailerServicePhone}`}>
                                    {retailerServicePhone}
                                </a>

                                <br />
                                <div className='openingDays'>
                                    {retailerServiceOpeningDays}
                                    <br />
                                    {retailerServiceOpeningHours}
                                </div>
                            </p>
                        </div>
                        <a title='phone' href={`tel:${retailerServicePhone}`}>
                            <DroletButton
                                className='contactBtn'
                                label={t('contact.call-us')}
                            />
                        </a>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
